import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import {
  api, getConnectionState, subscribeConnection, requestConnectionRetry, onConnectionRetry,
  startConnectionPing, configureConnection,
} from '@pilot-hub/api-client'
import type { ConnectionState, ConnectionError, ConnectionOptions } from '@pilot-hub/api-client'

/**
 * Anzeigestatus:
 *  unknown  grau   – noch keine Antwort
 *  ok       grün   – letzte Antwort jünger als staleMs
 *  stale    gelb   – Server meldet sich nicht (kein Fehler), aber letzte Antwort älter als staleMs
 *  degraded gelb   – ein Verbindungsfehler seit der letzten Antwort
 *  offline  rot    – mehrere Verbindungsfehler in Folge oder Browser offline
 */
export type ConnectionLevel = 'unknown' | 'ok' | 'stale' | 'degraded' | 'offline'

export interface ConnectionContextValue {
  level: ConnectionLevel
  lastSuccessAt: number | null
  lastError: ConnectionError | null
  failCount: number
  pending: number
  online: boolean
  /** Schwelle in ms, ab der Daten als veraltet gelten (0 = nie) */
  staleMs: number
  /** Alter der letzten Antwort in ms zum Zeitpunkt `now` (Tab-Hintergrund zählt nicht mit) */
  ageMs: number | null
  retry: () => void
  /** Seiten registrieren hier ihr Neuladen für "Erneut versuchen"; gibt die Abmelde-Funktion zurück */
  onRetry: (cb: () => void) => () => void
}

const Ctx = createContext<ConnectionContextValue | null>(null)

export interface ConnectionProviderProps {
  /** Ab diesem Alter (ms) der letzten Antwort gilt "Daten veraltet". Standard 60000; 0 = Prüfung aus. Pro Modul wählen (Leitstelle 20000). */
  staleMs?: number
  /** Leichter Ping (GET /auth/me) für Module ohne Polling (Admin). true = alle 30 s, Zahl = Intervall in ms. */
  ping?: boolean | number
  /** Feineinstellung des Interceptors (failThreshold, ignoreUrls, longRunningMs). */
  options?: ConnectionOptions
  children: React.ReactNode
}

export function useTick(intervalMs = 1000, active = true): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    setNow(Date.now())
    const t = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') setNow(Date.now())
    }, intervalMs)
    return () => clearInterval(t)
  }, [intervalMs, active])
  return now
}

export function ConnectionProvider({ staleMs = 60000, ping = false, options, children }: ConnectionProviderProps) {
  const s: ConnectionState = useSyncExternalStore(subscribeConnection, getConnectionState, getConnectionState)
  const optKey = options ? JSON.stringify(options) : ''
  useEffect(() => { if (options) configureConnection(options) }, [optKey]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ping) return
    return startConnectionPing(api, { intervalMs: typeof ping === 'number' ? ping : 30000 })
  }, [ping])

  // Zeit, seit der der Tab sichtbar ist: ein im Hintergrund gedrosselter Poll darf kein "veraltet" auslösen
  const [visibleSince, setVisibleSince] = useState(() => Date.now())
  useEffect(() => {
    const on = () => { if (document.visibilityState === 'visible') setVisibleSince(Date.now()) }
    document.addEventListener('visibilitychange', on)
    return () => document.removeEventListener('visibilitychange', on)
  }, [])

  const staleEnabled = staleMs > 0 && s.lastSuccessAt !== null
  const now = useTick(1000, staleEnabled)

  const value = useMemo<ConnectionContextValue>(() => {
    const ref = s.lastSuccessAt === null ? null : Math.max(s.lastSuccessAt, visibleSince)
    const ageMs = ref === null ? null : Math.max(0, now - ref)
    const isStale = staleMs > 0 && ageMs !== null && ageMs > staleMs
    let level: ConnectionLevel
    if (s.status === 'offline') level = 'offline'
    else if (s.status === 'degraded') level = 'degraded'
    else if (s.status === 'unknown') level = 'unknown'
    else level = isStale ? 'stale' : 'ok'
    return {
      level, lastSuccessAt: s.lastSuccessAt, lastError: s.lastError, failCount: s.failCount,
      pending: s.pending, online: s.online, staleMs, ageMs,
      retry: requestConnectionRetry, onRetry: onConnectionRetry,
    }
  }, [s, now, visibleSince, staleMs])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** Verbindungsstatus; ohne ConnectionProvider ein neutraler Wert (Level "unknown"). */
export function useConnection(): ConnectionContextValue {
  const v = useContext(Ctx)
  const fallback = useMemo<ConnectionContextValue>(() => ({
    level: 'unknown', lastSuccessAt: null, lastError: null, failCount: 0, pending: 0, online: true,
    staleMs: 0, ageMs: null, retry: requestConnectionRetry, onRetry: onConnectionRetry,
  }), [])
  return v ?? fallback
}

/**
 * Seite registriert ihr Neuladen für "Erneut versuchen" (StaleBanner/StatusBar):
 *   useConnectionRetry(() => laden())
 */
export function useConnectionRetry(cb: () => void) {
  const { onRetry } = useConnection()
  const ref = React.useRef(cb)
  ref.current = cb
  const stable = useCallback(() => ref.current(), [])
  useEffect(() => onRetry(stable), [onRetry, stable])
}
